import React from 'react';
import { InfoIcon, TagIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { LineItemRow } from './LineItemRow';
import { lineItemGroups } from '../data/quotation';
import { formatEuro, groupTotal, QuotationTotals } from '../utils/pricing';
interface InvestmentSectionProps {
  totals: QuotationTotals;
}
export function InvestmentSection({
  totals
}: InvestmentSectionProps) {
  return <section id="investment" className="border-t border-line bg-surface py-20 sm:py-28" data-id="element-47" data-mp="2gpb6syn7fmho">
      <div className="mx-auto w-full max-w-7xl px-5 sm:px-8" data-id="element-48" data-mp="2bqzzovoanq35">
        <SectionHeading eyebrow="The investment" title="Every line, at the quoted scope" description="Fixed pricing for the scope above. Open any line to see exactly what it includes." data-id="element-49" data-mp="270jkb60wxue5" />

        <div className="mt-12 space-y-10" data-id="element-50" data-mp="2zhc9yd70hyww">
          {lineItemGroups.map(group => <div key={group.id} data-id="element-51" data-mp="2uqdrp82gqqr0">
              <div className="flex flex-col gap-2 pb-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-52" data-mp="2upc8ggbamxy7">
                <div data-id="element-53" data-mp="13uedx853h692">
                  <h3 className="font-display text-xl font-semibold text-ink-900" data-id="element-54" data-mp="13v7q4omtonyl">
                    {group.title}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-ink-500" data-id="element-55" data-mp="u3tituomqjuw">
                    {group.subtitle}
                  </p>
                </div>
                <p className="shrink-0 font-display text-lg font-bold tracking-tight text-ink-900" data-id="element-56" data-mp="z0jzok89djhd">
                  {formatEuro(groupTotal(group))}
                </p>
              </div>
              <div className="overflow-hidden rounded-2xl border border-line bg-white" data-id="element-57" data-mp="p5s1pfarkum2">
                {group.items.map(item => <LineItemRow key={item.id} item={item} data-id="element-58" data-mp="g3irkqjer397" />)}
              </div>
            </div>)}
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-[1.4fr_1fr]" data-id="element-59" data-mp="25ji6ypdmdoxa">
          <div className="rounded-2xl border border-line bg-white p-6 sm:p-8" data-id="element-60" data-mp="2ah9xpg2j34zw">
            <h3 className="font-display text-lg font-semibold text-ink-900" data-id="element-61" data-mp="2af7af3srfpyv">
              How the investment phases
            </h3>
            <dl className="mt-6 space-y-4" data-id="element-62" data-mp="240rxrlvgqbha">
              <SummaryRow label="Campaign fees" value={totals.campaigns} note="Snakification, website and campaign localization, master campaigns and 12 months of country activation" data-id="element-63" data-mp="2400107q4q7pf" />
              <SummaryRow label="Control Tower licence" value={totals.annual} note="Annual, covering 3 brands and 5 countries" data-id="element-64" data-mp="2rrduih00o9bz" />
              <SummaryRow label="Central Hub adaptation" value={totals.integration} note="One-off integration with Leo Pharma Central Hub" data-id="element-65" data-mp="2i0sy907tfgw8" />
            </dl>
            <p className="mt-6 flex items-start gap-2 border-t border-line pt-5 text-xs leading-relaxed text-ink-300" data-id="element-66" data-mp="3ahn62hhp1dgc">
              <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-67" data-mp="35pvec3msx1om" />
              All prices in EUR, excluding VAT. Media spend, third-party
              licences and Leo Pharma MLR costs are not included.
            </p>
          </div>

          <div className="flex flex-col justify-between rounded-2xl bg-primary p-6 sm:p-8" data-id="element-68" data-mp="3mzze9dfl6m4x">
            <div data-id="element-69" data-mp="3rz2g6p09sc8o">
              <p className="text-xs uppercase tracking-[0.18em] text-white/70" data-id="element-70" data-mp="3rzlode04bm9i">
                Total year-one investment
              </p>
              <p className="mt-4 font-display text-5xl font-bold leading-none tracking-tight text-white" data-id="element-71" data-mp="3w04hdbdqyyda">
                {formatEuro(totals.total)}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-white/80" data-id="element-72" data-mp="9kt7e3v6dlgj">
                3 corporate brands · 5 master campaigns · 5 EU5 markets · 12
                months activation per campaign · Control Tower included.
              </p>
            </div>
            <p className="mt-8 text-sm text-white/70" data-id="element-73" data-mp="jhfrgqfog8hi">
              Year two onwards: {formatEuro(totals.annual)} platform licence
              plus agreed campaign volume.
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-primary bg-white p-6 sm:p-8" data-id="element-74" data-mp="1gavyja3masjj">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-6" data-id="element-75" data-mp="1g8t84oyjyxea">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary" data-id="element-76" data-mp="1u4538xgmepvq">
              <TagIcon className="h-5 w-5" aria-hidden="true" data-id="element-77" data-mp="12al642mnt5w9" />
            </span>
            <div data-id="element-78" data-mp="12cpi77uppb7m">
              <h3 className="font-display text-lg font-semibold text-ink-900" data-id="element-79" data-mp="xc1mw9zga87l">
                Volume discounts on the wider programme
              </h3>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-ink-500" data-id="element-80" data-mp="1bcugmshxgz9n">
                The pricing above is for Phase 1. Snakification and the Control
                Tower licence are paid once, so every additional country, brand
                and campaign runs off an investment already made. As soon as the
                total number of countries and brands across Phase 2 and Phase 3
                is known, we will reprice the programme with volume discounts
                applied across snakification, localization, master campaigns and
                country activation, and we are happy to fix those rates for the
                full commitment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
interface SummaryRowProps {
  label: string;
  value: number;
  note: string;
}
function SummaryRow({
  label,
  value,
  note
}: SummaryRowProps) {
  return <div className="flex items-start justify-between gap-6 border-b border-line pb-4 last:border-b-0 last:pb-0" data-id="element-81" data-mp="3e013zjthvwxf">
      <div data-id="element-82" data-mp="34856a9vbm8tq">
        <dt className="text-sm font-medium text-ink-900" data-id="element-83" data-mp="347vd6dezbljt">{label}</dt>
        <dd className="mt-1 text-xs text-ink-300" data-id="element-84" data-mp="34627p1g3r7de">{note}</dd>
      </div>
      <dd className="shrink-0 font-display text-base font-semibold text-ink-900" data-id="element-85" data-mp="3rwepr552akhp">
        {formatEuro(value)}
      </dd>
    </div>;
}